// Builds and uploads a shareable PDI report with full PDF parity: score,
// vehicle info, recommendation, and the complete section-by-section detail
// (statuses, notes, photos). Photos are downscaled client-side and capped by
// a total-size budget because the upload goes through a serverless function
// with a ~4.5MB request limit (the PDF has no such limit — it's generated
// on-device from local data).

import { CHECKLIST_SECTIONS } from '../data/checklistData';
import { FEATURE_BASED_CHECKS } from '../data/modelSpecificChecks';

const MAX_PHOTO_DIM = 1000;
const PHOTO_QUALITY = 0.7;
const MAX_PHOTOS_PER_ITEM = 4;
// Stay comfortably under Vercel's 4.5MB request-body cap.
const PHOTO_BUDGET_CHARS = 3_200_000;

const downscale = (dataUrl) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { naturalWidth: w, naturalHeight: h } = img;
      const scale = Math.min(1, MAX_PHOTO_DIM / Math.max(w, h));
      w = Math.round(w * scale);
      h = Math.round(h * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', PHOTO_QUALITY));
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });

// Same recommendation logic as the PDF's summary page.
const recommend = (score) => {
  if (score.fail > 0) return 'blocked';
  if (score.attention > 0) return 'caution';
  if (score.pending > score.total * 0.2) return 'incomplete';
  return 'ready';
};

/**
 * Assemble the full shareable report (payload v2) from raw inspection state.
 * Mirrors the PDF: every section appears, with its notable items (anything
 * failed, needing attention, noted, or photographed) in full detail.
 */
export const buildSharePayload = async ({
  pdiScore,
  carProfile,
  itemStatuses,
  notes,
  photos,
}) => {
  const features = carProfile?.features || [];
  const featureSections = features
    .filter((f) => FEATURE_BASED_CHECKS[f])
    .map((f) => ({
      title: FEATURE_BASED_CHECKS[f].name,
      items: FEATURE_BASED_CHECKS[f].items,
    }));

  const allSections = [
    ...CHECKLIST_SECTIONS.map((s) => ({ title: s.title, items: s.items })),
    ...featureSections,
  ];

  let photoChars = 0;
  const sections = [];

  for (const section of allSections) {
    const total = section.items.length;
    const completed = section.items.filter(
      (i) => itemStatuses[i.id] && itemStatuses[i.id] !== 'pending'
    ).length;

    // Same filter as the PDF's detailed pages: only items with something to say.
    const notable = section.items.filter((i) => {
      const status = itemStatuses[i.id];
      const hasNote = notes[i.id] && notes[i.id].trim() !== '';
      const hasPhotos = photos[i.id] && photos[i.id].length > 0;
      return hasNote || hasPhotos || status === 'fail' || status === 'attention';
    });

    const items = [];
    for (const item of notable) {
      const raw = (photos[item.id] || []).slice(0, MAX_PHOTOS_PER_ITEM);
      const shots = [];
      let skipped = 0;
      for (const p of raw) {
        if (photoChars >= PHOTO_BUDGET_CHARS) {
          skipped++;
          continue;
        }
        const shot = await downscale(p.data);
        if (shot) {
          photoChars += shot.length;
          shots.push(shot);
        }
      }
      items.push({
        title: item.title,
        status: itemStatuses[item.id] || 'pending',
        note: (notes[item.id] || '').trim(),
        photos: shots,
        morePhotos: skipped + Math.max(0, (photos[item.id] || []).length - MAX_PHOTOS_PER_ITEM),
      });
    }

    let summary = '';
    if (items.length === 0) {
      const percentage = total ? Math.round((completed / total) * 100) : 0;
      summary =
        percentage === 100
          ? 'All items passed — no issues found'
          : completed === 0
            ? 'No items checked yet'
            : 'Checked items passed — no notable issues';
    }

    sections.push({
      title: section.title,
      completed,
      total,
      percentage: total ? Math.round((completed / total) * 100) : 0,
      items,
      summary,
    });
  }

  const score = {
    percentage: pdiScore.scorePercentage,
    checked: pdiScore.checked,
    total: pdiScore.total,
    pass: pdiScore.pass,
    fail: pdiScore.fail,
    attention: pdiScore.attention,
    pending: pdiScore.pending,
  };

  return {
    v: 2,
    car: {
      make: carProfile?.carMake || '',
      model: carProfile?.carModel || '',
      variant: carProfile?.variant || '',
      color: carProfile?.color || '',
      vin: carProfile?.vin || '',
      dealer: carProfile?.dealerName || '',
    },
    score,
    recommendation: recommend(score),
    sections,
  };
};

/**
 * Upload a payload and return the shareable path (e.g. "/r/abc123").
 * Throws with a friendly message on failure.
 */
export const saveSharedReport = async (payload) => {
  const res = await fetch('/api/save-report', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(
      res.status === 503
        ? 'Report sharing is not set up yet. Please try again later.'
        : 'Could not create the share link. Please try again.'
    );
  }
  const { id } = await res.json();
  return `/r/${id}`;
};
